import React, { useEffect, useState } from "react";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, NAV_LINKS, buildWhatsAppUrl } from "@/lib/constants";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/landing/Logo";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 h-20 lg:h-24 flex items-center justify-between gap-3">
        <button
          className="lg:hidden p-2 -ml-2 rounded-md text-slate-800 hover:bg-slate-100 shrink-0"
          data-testid="navbar-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <a href="#home" data-testid="navbar-logo" className="shrink-0">
          <Logo heightClass="h-14 lg:h-16" />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <a
              key={`${link.label}-${link.href}`}
              href={link.href}
              data-testid={`navbar-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-sm font-medium text-[#0F172A] hover:text-[#19A8E8] transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="navbar-phone-link"
            className="hidden md:flex items-center gap-2 h-10 px-3 rounded-full border border-slate-200 hover:border-[#19A8E8] hover:bg-[#F8FAFC] transition-all text-[#0F172A]"
          >
            <Phone className="h-3.5 w-3.5 text-[#19A8E8]" />
            <span className="text-sm font-semibold">{COMPANY.phoneDisplay}</span>
          </a>
          <a
            href={buildWhatsAppUrl("Hi Cabsab.com, I'd like to book a cab.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-all"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <Button
            asChild
            data-testid="navbar-book-now-btn"
            className="rounded-full bg-[#19A8E8] hover:bg-[#0E8AC4] text-white font-semibold h-10 px-4 lg:px-5 shadow-[0_4px_14px_rgba(25,168,232,0.30)] transition-all"
          >
            <a href="#booking">Book Now</a>
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-slate-200"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={`m-${link.label}`}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium text-[#0F172A] py-2.5 border-b border-slate-100 hover:text-[#19A8E8]"
                  data-testid={`navbar-mobile-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
